import { useState, useEffect, useCallback } from "react";
import { AttendanceService } from "@/services/Attendance/AttendanceService";
import { SalaryHeadServices } from "@/services/SalaryHead/SalaryHeadServices";
import type { IPageInfo } from "@/interface/common.interface";
import type {
  IAttendanceSummaryUser,
  ILockAttendancePayload,
} from "@/interface/attendance.interface";
import { ThrowError } from "@/api/callApi";
import { showToastNotification } from "@/utility/helper";
import { createInitialPageInfo } from "./createSalary.constants";
import {
  buildAttendanceQuery,
  getAttendanceLockState,
  getPageInfoUpdate,
  getSelectedUserIds,
  hasPendingAttendanceRequests,
  resetPage,
} from "./createSalary.utils";
import type { UseMasterDataReturn } from "./useMasterData";
import { PayslipService } from "@/services/Payslip/PayslipService";

/**
 * Attendance tab hook.
 *
 * `selectedEmployeeIdsRef` is a ref so the lock handler always reads
 * the latest selection without being a hook dependency (avoids circular deps).
 */
export const useAttendanceTab = (
  masterData: UseMasterDataReturn,
  selectedEmployeeIdsRef: React.RefObject<Set<string | number>>,
) => {
  const [attendanceList, setAttendanceList] = useState<
    IAttendanceSummaryUser[]
  >([]);
  const [isLoadingAttendance, setIsLoadingAttendance] =
    useState<boolean>(false);
  const [isAttendanceLocked, setIsAttendanceLocked] = useState<boolean>(false);
  const [isLockingAttendance, setIsLockingAttendance] =
    useState<boolean>(false);
  const [downloadingSalaryId, setDownloadingSalaryId] = useState<string | null>(
    null,
  );
  const [attendancePaginationData, setAttendancePaginationData] =
    useState<IPageInfo>(createInitialPageInfo);

  const fetchAttendanceSummary = useCallback(
    async (
      currentPage = attendancePaginationData.currentPage,
      currentLimit = attendancePaginationData.pageSize,
    ) => {
      if (!masterData.selectedBranchId && !masterData.selectedSiteId) return;

      setIsLoadingAttendance(true);
      try {
        const response = await AttendanceService.getDailyTeamAttendance(
          buildAttendanceQuery(
            {
              selectedBranchId: masterData.selectedBranchId,
              selectedSiteId: masterData.selectedSiteId,
              selectedCategory: masterData.selectedCategory,
              searchValue: masterData.searchValue,
              attendanceStartDate: masterData.attendanceStartDate,
              attendanceEndDate: masterData.attendanceEndDate,
            },
            { currentPage, currentLimit },
          ),
        );

        if (response?.status && response?.data) {
          const usersList = response.data.users || [];
          setAttendanceList(usersList);

          setIsAttendanceLocked(
            getAttendanceLockState(usersList, response.data.attendanceLock),
          );

          setAttendancePaginationData((prev) => ({
            ...prev,
            ...getPageInfoUpdate(
              response.data.pageInfo,
              usersList.length,
              currentLimit,
            ),
          }));
        } else {
          setAttendanceList([]);
          setIsAttendanceLocked(false);
          setAttendancePaginationData((prev) => ({
            ...prev,
            total: 0,
            totalPages: 0,
          }));
        }
      } catch (error) {
        ThrowError(error);
        setAttendanceList([]);
        setIsAttendanceLocked(false);
      } finally {
        setIsLoadingAttendance(false);
      }
    },
    [
      masterData.selectedBranchId,
      masterData.selectedSiteId,
      masterData.selectedCategory,
      masterData.searchValue,
      masterData.attendanceStartDate,
      masterData.attendanceEndDate,
      attendancePaginationData.currentPage,
      attendancePaginationData.pageSize,
    ],
  );

  // Fetch when tab becomes active
  useEffect(() => {
    if (masterData.activeTab === "attendance") fetchAttendanceSummary();
  }, [masterData.activeTab, fetchAttendanceSummary]);

  // Validate that users have salary configuration before locking attendance
  const validateUsersSalaryConfig = useCallback(
    async (targetUserIds: string[]): Promise<boolean> => {
      try {
        const queryParams = new URLSearchParams();
        if (masterData.selectedBranchId)
          queryParams.append("branchId", masterData.selectedBranchId);
        if (masterData.selectedSiteId)
          queryParams.append("siteId", masterData.selectedSiteId);
        if (masterData.selectedCategory)
          queryParams.append("category", masterData.selectedCategory);
        queryParams.append("pageNumber", "1");
        queryParams.append("pageSize", "10000");

        const response = await SalaryHeadServices.getBranchWiseUserSalary(
          queryParams.toString(),
        );

        if (response?.status || response?.statuscode === 200) {
          const usersList = response?.data?.users || response?.data?.user || [];
          const configuredMap = new Map<string, boolean>();
          const nameMap = new Map<string, string>();

          usersList.forEach((u: any) => {
            const hasSalaryConfig =
              Array.isArray(u.salaries) && u.salaries.length > 0;
            if (u.id) {
              configuredMap.set(String(u.id), hasSalaryConfig);
              nameMap.set(String(u.id), u.name || "Employee");
            }
          });

          const unconfiguredList: string[] = [];
          targetUserIds.forEach((uid) => {
            if (!configuredMap.get(uid)) {
              unconfiguredList.push(nameMap.get(uid) || `ID: ${uid}`);
            }
          });

          if (unconfiguredList.length > 0) {
            const count = unconfiguredList.length;
            const names = unconfiguredList.slice(0, 3).join(", ");
            const more = count > 3 ? ` and ${count - 3} more` : "";
            showToastNotification(
              "danger",
              `Cannot lock attendance: Salary is not configured for ${count} employee${count > 1 ? "s" : ""} (${names}${more}). Please configure their salary heads in Step 1 first.`,
            );
            return false;
          }
        }
        return true;
      } catch (error) {
        console.error("Error validating salary config before locking:", error);
        return true;
      }
    },
    [
      masterData.selectedBranchId,
      masterData.selectedSiteId,
      masterData.selectedCategory,
    ],
  );

  // Lock attendance handler — reads selection via ref to avoid circular dependency
  const handleLockAttendance = useCallback(async () => {
    const currentIds = selectedEmployeeIdsRef.current ?? new Set();
    const selectedUserIds = getSelectedUserIds(currentIds);

    const targetList =
      selectedUserIds.length > 0
        ? attendanceList.filter(
            (item) =>
              item.user?.id && selectedUserIds.includes(String(item.user.id)),
          )
        : attendanceList;

    if (hasPendingAttendanceRequests(targetList)) {
      showToastNotification(
        "danger",
        "Attendance cannot be locked because there are pending attendance requests.",
      );
      return;
    }

    const targetUserIds = targetList
      .map((item) => String(item.user?.id))
      .filter(Boolean);

    const isSalaryValid = await validateUsersSalaryConfig(targetUserIds);
    if (!isSalaryValid) return;

    setIsLockingAttendance(true);
    try {
      const payload: ILockAttendancePayload = {
        userIds: selectedUserIds,
        startDate: masterData.attendanceStartDate,
        endDate: masterData.attendanceEndDate,
        siteIds: masterData.selectedSiteId || "",
      };

      const response = await AttendanceService.lockAttendance(payload);
      if (response?.status) {
        showToastNotification(
          "success",
          response?.message || "Attendance locked successfully!",
        );
        fetchAttendanceSummary();
      } else {
        showToastNotification(
          "danger",
          response?.message || "Failed to lock attendance.",
        );
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLockingAttendance(false);
    }
  }, [
    selectedEmployeeIdsRef,
    attendanceList,
    validateUsersSalaryConfig,
    masterData.attendanceStartDate,
    masterData.attendanceEndDate,
    masterData.selectedSiteId,
    fetchAttendanceSummary,
  ]);

  const handleAttendanceChangePageSize = useCallback((newSize: number) => {
    setAttendancePaginationData((prev) => ({
      ...prev,
      pageSize: newSize,
      currentPage: 1,
    }));
  }, []);

  const resetPagination = useCallback(() => {
    setAttendancePaginationData(resetPage);
  }, []);

  // Salary slip download handler
  const handleDownloadSalarySlip = useCallback(
    async (userId: string) => {
      if (!masterData.attendanceStartDate || !masterData.attendanceEndDate) {
        showToastNotification(
          "danger",
          "Please select a valid date range first.",
        );
        return;
      }
      try {
        setDownloadingSalaryId(userId);
        const arrayBuffer = await PayslipService.generatePayslip(
          userId,
          masterData.attendanceStartDate,
          masterData.attendanceEndDate,
        );

        if (!arrayBuffer) {
          showToastNotification("danger", "Failed to generate salary slip.");
          return;
        }

        const blob = new Blob([arrayBuffer], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `salary_slip_${userId}_${masterData.attendanceStartDate}_${masterData.attendanceEndDate}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        showToastNotification(
          "success",
          "Salary slip downloaded successfully!",
        );
      } catch (error) {
        ThrowError(error);
      } finally {
        setDownloadingSalaryId(null);
      }
    },
    [masterData.attendanceStartDate, masterData.attendanceEndDate],
  );

  const [showLockAttendanceModal, setShowLockAttendanceModal] =
    useState<boolean>(false);

  const handleOpenLockAttendanceModal = useCallback(async () => {
    const currentIds = selectedEmployeeIdsRef.current ?? new Set();
    const selectedUserIds = getSelectedUserIds(currentIds);
    const targetList =
      selectedUserIds.length > 0
        ? attendanceList.filter(
            (item) =>
              item.user?.id && selectedUserIds.includes(String(item.user.id)),
          )
        : attendanceList;

    if (hasPendingAttendanceRequests(targetList)) {
      showToastNotification(
        "danger",
        "Attendance cannot be locked because there are pending attendance requests.",
      );
      return;
    }

    const targetUserIds = targetList
      .map((item) => String(item.user?.id))
      .filter(Boolean);

    const isSalaryValid = await validateUsersSalaryConfig(targetUserIds);
    if (!isSalaryValid) return;

    setShowLockAttendanceModal(true);
  }, [selectedEmployeeIdsRef, attendanceList, validateUsersSalaryConfig]);

  const handleCloseLockAttendanceModal = useCallback(() => {
    setShowLockAttendanceModal(false);
  }, []);

  const handleConfirmLockAttendance = useCallback(async () => {
    await handleLockAttendance();
    setShowLockAttendanceModal(false);
  }, [handleLockAttendance]);

  return {
    attendanceList,
    isLoadingAttendance,
    isAttendanceLocked,
    isLockingAttendance,
    showLockAttendanceModal,
    attendancePaginationData,
    setAttendancePaginationData,
    handleLockAttendance,
    handleOpenLockAttendanceModal,
    handleCloseLockAttendanceModal,
    handleConfirmLockAttendance,
    handleAttendanceChangePageSize,
    handleDownloadSalarySlip,
    downloadingSalaryId,
    resetPagination,
    refetchAttendanceSummary: fetchAttendanceSummary,
  };
};

export type UseAttendanceTabReturn = ReturnType<typeof useAttendanceTab>;
